<script>
  import { onMount } from "svelte";
  import {
    Map,
    Marker,
    GeolocateControl,
    NavigationControl,
    ScaleControl,
    Layer,
    GeoJSONSource,
  } from "svelte-mapbox-gl";
  import { Geocoder } from "@beyonk/svelte-mapbox";
  import { selectedDistrict } from "@stores/userData.js";
  import { points, feature, pointsWithinPolygon } from "@turf/turf";

  export let center;
  export let zoom;

  const token =
    "pk.eyJ1IjoicGFuZGFmdWxtYW5kYSIsImEiOiJjbDFqbW54NzYwYmRwM2pxZzRudGdxY2ZkIn0.Q6piGfurn0JkQ6tg9qiL9A";

  // Define this to handle `eventname` events - see [GeoLocate Events](https://docs.mapbox.com/mapbox-gl-js/api/markers/#geolocatecontrol-events)
  function eventHandler(e) {
    const data = e.detail;
    // console.log("geocoder", data);
    // do something with `data`, it's the result returned from the mapbox event
  }

  function setActiveDistrict(mouseenterEvent) {
    // console.log("enter", mouseenterEvent.detail.features);
    selectedDistrict.set(
      mouseenterEvent.detail.features[0].properties.district
    );
  }

  function unsetActiveDistrict(mouseleaveEvent) {
    // console.log("exit", mouseleaveEvent.detail.features);
    selectedDistrict.set(null);
  }

  import cityCouncilDistrictGeoJSON from "@data/city-council-districts.geojson";

  const cityCouncilDistrictFeatures = cityCouncilDistrictGeoJSON.features.map(
    (item) => feature(item.geometry, item.properties)
  );

  let layoutLabel = {
    "text-field": ["get", "name"],
    "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"],
    "text-size": 14,
    "text-padding": ["interpolate", ["linear"], ["zoom"], 1, 10, 7, 20, 9, 100],
  };
  let filter = ["has", "district"];

  $: paintFill = {
    "fill-color": [
      "match",
      ["get", "district"],
      "A",
      "hsla(58, 80%, 61%, 1)",
      "B",
      "hsla(73, 65%, 62%, 1)",
      "C",
      "hsla(93, 43%, 62%, 1)",
      "D",
      "hsla(134, 21%, 56%, 1)",
      "E",
      "hsla(198, 21%, 45%, 1)",
      "F",
      "hsla(232, 32%, 38%, 1)",
      "G",
      "hsla(266, 85%, 21%, 1)",
      "H",
      "hsla(257, 100%, 75%, 1)",
      "I",
      "hsla(248, 100%, 68%, 1)",
      "J",
      "hsla(223, 100%, 67%, 1)",
      "K",
      "hsla(202, 100%, 67%, 1)",
      "hsla(0, 0%, 0%, 0)",
    ],
    "fill-opacity":
      ($selectedDistrict && [
        "match",
        ["get", "district"],
        $selectedDistrict,
        1,
        0.25,
      ]) ||
      1,
  };
  let paintStroke = {
    "line-width": 2,
    "line-color": [
      "match",
      ["get", "district"],
      "A",
      "hsl(58, 80%, 43%)",
      "B",
      "hsl(73, 65%, 43%)",
      "C",
      "hsl(93, 43%, 43%)",
      "D",
      "hsl(134, 21%, 39%)",
      "E",
      "hsl(198, 21%, 32%)",
      "F",
      "hsl(232, 32%, 27%)",
      "G",
      "hsl(266, 85%, 15%)",
      "hsla(0, 0%, 0%, 0)",
    ],
  };
  let paintLabel = {
    "text-color": [
      "match",
      ["get", "district"],
      "A",
      "hsl(58, 80%, 37%)",
      "B",
      "hsl(73, 65%, 37%)",
      "C",
      "hsl(93, 43%, 37%)",
      "D",
      "hsl(134, 21%, 34%)",
      "E",
      "hsl(198, 21%, 27%)",
      "F",
      "hsl(232, 32%, 23%)",
      "G",
      "hsl(266, 85%, 13%)",
      "hsla(0, 0%, 0%, 0)",
    ],
  };
  const NORTH = 30.166256;
  const EAST = -94.920227;
  const SOUTH = 29.490625;
  const WEST = -95.962188;
  const boundingBox = [WEST, SOUTH, EAST, NORTH];
  let map;
  let searchCoordinates;

  function matchAndSetDistrict(coordinates) {
    const resultPoint = points([coordinates]);
    const foundDistrict = cityCouncilDistrictFeatures.find(
      (feature) => pointsWithinPolygon(resultPoint, feature).features.length > 0
    );
    if (foundDistrict) {
      selectedDistrict.set(foundDistrict.properties.district);
    }
  }

  function setSearchCoordinates(result) {
    searchCoordinates = result.detail.result.center;
    map.easeTo({
      center: searchCoordinates,
      zoom: 10,
    });
    matchAndSetDistrict(searchCoordinates);
  }
</script>

<Map
  accessToken={token}
  style="mapbox://styles/mapbox/light-v10"
  scrollZoom={false}
  dragPan={false}
  bind:center
  bind:zoom
  bind:map
>
  <!-- MINOR FIXME: Would be nice to have this default filter to Houston-only addresses -->
  <Geocoder
    options={{
      bbox: boundingBox,
    }}
    accessToken={token}
    on:result={setSearchCoordinates}
  />
  {#if searchCoordinates}
    <Marker coordinates={searchCoordinates} />
  {/if}
  <GeoJSONSource
    id="cityCouncilDistrictGeoJSON"
    data={cityCouncilDistrictGeoJSON}
  >
    <Layer
      id="cityCouncilFill"
      type="fill"
      source="cityCouncilDistrictGeoJSON"
      paint={paintFill}
      {filter}
      on:click={setActiveDistrict}
    />
    <Layer
      id="cityCouncilLine"
      type="line"
      source="cityCouncilDistrictGeoJSON"
      paint={paintStroke}
      {filter}
    />
    <Layer
      id="cityCouncilLabel"
      type="symbol"
      source="cityCouncilDistrictGeoJSON"
      paint={paintLabel}
      layout={layoutLabel}
      {filter}
    />
  </GeoJSONSource>
  <NavigationControl />
  <!-- <GeolocateControl
    options={{ some: "control-option" }}
    on:geolocate={eventHandler}
  /> -->
  <ScaleControl />
</Map>

<style>
  :global(.mapboxgl-canvas) {
    cursor: pointer;
  }

  :global(.mapboxgl-ctrl-geocoder) {
    z-index: 3 !important;
  }

  :global(.mapboxgl-ctrl-geocoder .suggestions) {
    top: 100% !important;
  }

  @media screen and (max-width: 640px) {
    :global(.mapboxgl-ctrl-top-right) {
      top: 50px !important;
    }
  }
</style>
